import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CalendarClock,
  ChevronRight,
  ChevronDown,
  ChevronsLeft,
  Home,
  Zap,
  Inbox,
  Calendar,
  Kanban,
  Settings,
  Check,
  Menu,
  Moon,
  Sun,
  LogOut,
  Layers,
  FileText,
  BarChart3,
} from 'lucide-react'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/hooks/useAuth'
import { useTheme } from '@/hooks/useTheme'
import { useProjects } from '@/hooks/useProjects'
import { useProjectScope } from '@/hooks/useProjectScope'
import { useQueryClient } from '@tanstack/react-query'
import { CommandPalette } from '@/components/CommandPalette'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'

interface NavItem {
  labelKey: string
  path: string
  icon: React.ElementType
}

interface ExpandableItem {
  labelKey: string
  items: { labelKey: string; path: string }[]
}

const mainNavItems: NavItem[] = [
  { labelKey: 'mainLayout.nav.home', path: '/', icon: Home },
  { labelKey: 'mainLayout.nav.inbox', path: '/inbox', icon: Inbox },
  { labelKey: 'mainLayout.nav.actions', path: '/actions', icon: Zap },
  { labelKey: 'mainLayout.nav.calendar', path: '/calendar', icon: Calendar },
  { labelKey: 'mainLayout.nav.booking', path: '/calendar/booking', icon: CalendarClock },
  { labelKey: 'mainLayout.nav.tasks', path: '/tasks', icon: Kanban },
  { labelKey: 'mainLayout.nav.pages', path: '/pages', icon: FileText },
  { labelKey: 'mainLayout.nav.analytics', path: '/analytics', icon: BarChart3 },
]

const expandableItems: ExpandableItem[] = [
  {
    labelKey: 'mainLayout.nav.pipelines',
    items: [
      { labelKey: 'mainLayout.nav.sales', path: '/pipelines/sales' },
      { labelKey: 'mainLayout.nav.support', path: '/pipelines/support' },
    ],
  },
  {
    labelKey: 'mainLayout.nav.records',
    items: [
      { labelKey: 'mainLayout.nav.contacts', path: '/records/contacts' },
      { labelKey: 'mainLayout.nav.companies', path: '/records/companies' },
    ],
  },
]

function getInitials(name?: string, email?: string) {
  const base = name?.trim() || email?.trim() || 'U'
  const parts = base.split(/\s+/).filter(Boolean)
  if (parts.length === 0) return 'U'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

function SidebarContent({ 
  onItemClick,
  isCollapsed = false,
  onToggleCollapse
}: { 
  onItemClick?: () => void
  isCollapsed?: boolean
  onToggleCollapse?: () => void
}) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [expandedItems, setExpandedItems] = useState<string[]>([])
  const { user, logout } = useAuth()
  const { resolvedTheme, setTheme } = useTheme()
  const queryClient = useQueryClient()
  
  // Project scope
  const { data: projectsResponse } = useProjects({ status: 'active', page_size: 50 })
  const projects = useMemo(() => projectsResponse?.items ?? [], [projectsResponse])
  const { scope, selectedProject, setScope } = useProjectScope(projects)

  const initials = useMemo(
    () => getInitials(user?.name, user?.email),
    [user?.name, user?.email]
  )

  const handleProjectChange = (projectId: string) => {
    setScope(projectId)
    queryClient.invalidateQueries()
  }

  const toggleExpand = (labelKey: string) => {
    setExpandedItems((prev) =>
      prev.includes(labelKey)
        ? prev.filter((item) => item !== labelKey)
        : [...prev, labelKey]
    )
  }

  const isActive = (path: string) => location.pathname === path

  return (
    <div className="flex flex-col h-full">
      {/* Header - Project Scope Selector */}
      <div className={`${isCollapsed ? 'px-2' : 'px-4'} py-4`}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button 
              title={isCollapsed ? (selectedProject?.name || t('mainLayout.projects.allProjects')) : undefined}
              className={`flex items-center gap-3 ${isCollapsed ? 'justify-center px-2' : 'px-3'} py-2 rounded-lg hover:bg-[hsl(var(--sidebar-accent))] transition-colors w-full`}
            >
              {/* Project color indicator or icon */}
              <div 
                className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                style={{ 
                  backgroundColor: selectedProject?.color 
                    ? `${selectedProject.color}20` 
                    : 'hsl(var(--sidebar-primary))' 
                }}
              >
                {selectedProject ? (
                  <span 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: selectedProject.color || '#64748b' }}
                  />
                ) : (
                  <Layers className="w-4 h-4 text-[hsl(var(--sidebar-primary-foreground))]" />
                )}
              </div>
              <AnimatePresence mode="wait">
                {!isCollapsed && (
                  <>
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.15, ease: 'easeOut' }}
                      className="text-sm font-medium text-[hsl(var(--sidebar-foreground))] truncate flex-1 text-left whitespace-nowrap overflow-hidden"
                    >
                      {selectedProject?.name || t('mainLayout.projects.allProjects')}
                    </motion.span>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.1 }}
                    >
                      <ChevronDown className="w-4 h-4 text-[hsl(var(--sidebar-foreground))] opacity-50 shrink-0" />
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            {/* All Projects option */}
            <DropdownMenuItem
              className="flex items-center gap-2"
              onClick={() => handleProjectChange('all')}
            >
              {scope === 'all' && <Check className="w-4 h-4" />}
              <Layers className={`w-4 h-4 ${scope !== 'all' ? 'ml-6' : ''}`} />
              <span>{t('mainLayout.projects.allProjects')}</span>
            </DropdownMenuItem>
            
            {projects.length > 0 && <DropdownMenuSeparator />}
            
            {/* Project list */}
            {projects.map((project) => (
              <DropdownMenuItem
                key={project.id}
                className="flex items-center gap-2"
                onClick={() => handleProjectChange(project.id)}
              >
                {scope === project.id && <Check className="w-4 h-4" />}
                <span 
                  className={`w-3 h-3 rounded-full ${scope !== project.id ? 'ml-6' : ''}`}
                  style={{ backgroundColor: project.color || '#64748b' }}
                />
                <span className="truncate">{project.name}</span>
              </DropdownMenuItem>
            ))}
            
            {projects.length === 0 && (
              <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                {t('mainLayout.projects.noProjects')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Main Navigation */}
      <nav className={`flex-1 ${isCollapsed ? 'px-2' : 'px-4'} space-y-1 overflow-y-auto`}>
        {mainNavItems.map((item) => {
          const Icon = item.icon
          const active = isActive(item.path)
          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onItemClick}
              title={isCollapsed ? t(item.labelKey) : undefined}
              className={`flex items-center gap-3 ${isCollapsed ? 'justify-center px-2' : 'px-3'} py-2 rounded-lg text-sm transition-colors ${
                active
                  ? 'bg-[hsl(var(--sidebar-accent))] text-[hsl(var(--sidebar-accent-foreground))] font-medium'
                  : 'text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))] hover:text-[hsl(var(--sidebar-accent-foreground))]'
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <AnimatePresence mode="wait">
                {!isCollapsed && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                    className="whitespace-nowrap overflow-hidden"
                  >
                    {t(item.labelKey)}
                  </motion.span>
                )}
              </AnimatePresence>
            </NavLink>
          )
        })}

        {/* Expandable Items */}
        <AnimatePresence mode="wait">
          {!isCollapsed && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="pt-6 space-y-1 overflow-hidden"
            >
              {expandableItems.map((item) => {
                const isExpanded = expandedItems.includes(item.labelKey)
                return (
                  <div key={item.labelKey}>
                    <button
                      onClick={() => toggleExpand(item.labelKey)}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))] hover:text-[hsl(var(--sidebar-accent-foreground))] transition-colors w-full"
                    >
                      <span className="flex-1 text-left">{t(item.labelKey)}</span>
                      <motion.div
                        animate={{ rotate: isExpanded ? 90 : 0 }}
                        transition={{ duration: 0.15 }}
                      >
                        <ChevronRight className="w-4 h-4 shrink-0" />
                      </motion.div>
                    </button>
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div 
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2, ease: 'easeOut' }}
                          className="ml-4 mt-1 space-y-1 border-l border-[hsl(var(--sidebar-border))] pl-4 overflow-hidden"
                        >
                          {item.items.map((subItem) => (
                            <NavLink
                              key={subItem.path}
                              to={subItem.path}
                              onClick={onItemClick}
                              className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive(subItem.path)
                                  ? 'bg-[hsl(var(--sidebar-accent))] text-[hsl(var(--sidebar-accent-foreground))] font-medium'
                                  : 'text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))] hover:text-[hsl(var(--sidebar-accent-foreground))]'
                              }`}
                            >
                              {t(subItem.labelKey)}
                            </NavLink>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Footer - Command Palette Trigger, Theme, Settings, User */}
      <div className={`${isCollapsed ? 'px-2' : 'px-4'} py-4 border-t border-[hsl(var(--sidebar-border))] space-y-1`}>
        {/* Command Palette Trigger */}
        <AnimatePresence mode="wait">
          {!isCollapsed && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={() => {
                const event = new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true })
                window.dispatchEvent(event)
              }}
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))] hover:text-[hsl(var(--sidebar-accent-foreground))] transition-colors w-full"
            >
              <span className="flex items-center gap-1.5 text-xs">
                <kbd className="rounded-md border border-[hsl(var(--sidebar-border))] bg-[hsl(var(--sidebar-accent))] px-2 py-1 font-medium">⌘</kbd>
                <kbd className="rounded-md border border-[hsl(var(--sidebar-border))] bg-[hsl(var(--sidebar-accent))] px-2 py-1 font-medium">K</kbd>
              </span>
            </motion.button>
          )}
        </AnimatePresence>

        {/* Theme Toggle */}
        <button
          onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
          title={isCollapsed ? (resolvedTheme === 'dark' ? t('mainLayout.theme.light') : t('mainLayout.theme.dark')) : undefined}
          className={`flex items-center gap-3 ${isCollapsed ? 'justify-center px-2' : 'px-3'} py-2 rounded-lg text-sm text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))] hover:text-[hsl(var(--sidebar-accent-foreground))] transition-colors w-full`}
        >
          {resolvedTheme === 'dark' ? (
            <Sun className="w-4 h-4 shrink-0" />
          ) : (
            <Moon className="w-4 h-4 shrink-0" />
          )}
          <AnimatePresence mode="wait">
            {!isCollapsed && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className="whitespace-nowrap overflow-hidden"
              >
                {resolvedTheme === 'dark' ? t('mainLayout.theme.light') : t('mainLayout.theme.dark')}
              </motion.span>
            )}
          </AnimatePresence>
        </button>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button 
              title={isCollapsed ? user?.name || user?.email || 'User' : undefined}
              className={`flex items-center gap-3 ${isCollapsed ? 'justify-center px-2' : 'px-3'} py-2 rounded-lg text-sm text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))] hover:text-[hsl(var(--sidebar-accent-foreground))] transition-colors w-full`}
            >
              <Avatar className="w-6 h-6 shrink-0">
                <AvatarFallback className="bg-[hsl(var(--sidebar-primary))] text-[hsl(var(--sidebar-primary-foreground))] text-[10px]">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <AnimatePresence mode="wait">
                {!isCollapsed && (
                  <>
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.15, ease: 'easeOut' }}
                      className="flex-1 text-left truncate whitespace-nowrap overflow-hidden"
                    >
                      {user?.name || user?.email || 'User'}
                    </motion.span>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.1 }}
                    >
                      <ChevronDown className="w-4 h-4 opacity-50 shrink-0" />
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top" className="w-52">
            <div className="px-2 py-1.5 text-xs text-[hsl(var(--muted-foreground))]">
              {user?.email}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => { navigate('/settings'); onItemClick?.() }}>
              <Settings className="w-4 h-4 mr-2" />
              {t('mainLayout.nav.settings')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => logout()} className="text-red-500 focus:text-red-500">
              <LogOut className="w-4 h-4 mr-2" />
              {t('mainLayout.user.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Collapse Toggle Button */}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            title={isCollapsed ? t('mainLayout.sidebar.expand', 'Expand sidebar') : t('mainLayout.sidebar.collapse', 'Collapse sidebar')}
            className={`flex items-center gap-3 ${isCollapsed ? 'justify-center px-2' : 'px-3'} py-2 rounded-lg text-sm text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))] hover:text-[hsl(var(--sidebar-accent-foreground))] transition-colors w-full focus:outline-none focus-visible:ring-1 focus-visible:ring-[hsl(var(--sidebar-ring))]`}
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4 shrink-0" />
            ) : (
              <ChevronsLeft className="w-4 h-4 shrink-0" />
            )}
            <AnimatePresence mode="wait">
              {!isCollapsed && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className="whitespace-nowrap overflow-hidden"
                >
                  {t('mainLayout.sidebar.collapse', 'Collapse')}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        )}
      </div>
    </div>
  )
}

// Sidebar collapse context
import { createContext, useContext } from 'react'

const SidebarCollapseContext = createContext<{
  isCollapsed: boolean
  setIsCollapsed: (value: boolean) => void
}>({
  isCollapsed: false,
  setIsCollapsed: () => {},
})

const useSidebarCollapse = () => useContext(SidebarCollapseContext)

function Sidebar() {
  const { isCollapsed, setIsCollapsed } = useSidebarCollapse()

  return (
    <motion.aside
      initial={false}
      animate={{
        width: isCollapsed ? 72 : 260,
      }}
      transition={{
        duration: 0.2,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      className="hidden md:flex h-screen bg-[hsl(var(--sidebar-background))] border-r border-[hsl(var(--sidebar-border))] flex-col fixed left-0 top-0 overflow-hidden"
    >
      <SidebarContent isCollapsed={isCollapsed} onToggleCollapse={() => setIsCollapsed(!isCollapsed)} />
    </motion.aside>
  )
}

function SidebarCollapseProvider({ children }: { children: React.ReactNode }) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem('go-crm:sidebar:collapsed') === 'true'
  })

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('go-crm:sidebar:collapsed', String(isCollapsed))
    }
  }, [isCollapsed])

  return (
    <SidebarCollapseContext.Provider value={{ isCollapsed, setIsCollapsed }}>
      {children}
    </SidebarCollapseContext.Provider>
  )
}

function MobileMenuTrigger() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <div className="md:hidden fixed top-3 left-3 z-50">
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetTrigger asChild>
          <button className="w-10 h-10 flex items-center justify-center rounded-md bg-[hsl(var(--background))] border border-[hsl(var(--border))] shadow-sm hover:bg-[hsl(var(--accent))]">
            <Menu className="w-5 h-5" />
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="p-0 w-[260px] bg-[hsl(var(--sidebar-background))] border-r border-[hsl(var(--sidebar-border))]">
          <SidebarContent onItemClick={() => setMobileMenuOpen(false)} />
        </SheetContent>
      </Sheet>
    </div>
  )
}

// Page transition variants
const pageVariants = {
  initial: {
    opacity: 0,
    y: 8,
  },
  enter: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
      ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number], // easeOutQuad
    },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: {
      duration: 0.15,
      ease: [0.55, 0.055, 0.675, 0.19] as [number, number, number, number], // easeInQuad
    },
  },
}

// Animated page wrapper component
function AnimatedPage({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        variants={pageVariants}
        initial="initial"
        animate="enter"
        exit="exit"
        className="flex-1"
      >
        {children}
      </motion.div>
    </AnimatePresence>
  )
}

function MainContent() {
  const { isCollapsed } = useSidebarCollapse()
  
  return (
    <motion.div 
      initial={false}
      animate={{ marginLeft: isCollapsed ? 72 : 260 }}
      transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] }}
      className="hidden md:flex flex-1 min-w-0 flex-col min-h-screen"
    >
      <main className="flex-1 overflow-auto">
        <AnimatedPage>
          <Outlet />
        </AnimatedPage>
      </main>
    </motion.div>
  )
}

export function MainLayout() {
  return (
    <SidebarCollapseProvider>
      <div className="flex min-h-screen bg-[hsl(var(--background))]">
        <Sidebar />
        <MobileMenuTrigger />
        <CommandPalette />
        {/* Mobile content */}
        <div className="flex-1 min-w-0 md:hidden flex flex-col min-h-screen">
          <main className="flex-1 overflow-auto pt-14">
            <AnimatedPage>
              <Outlet />
            </AnimatedPage>
          </main>
        </div>
        {/* Desktop content with animated margin */}
        <MainContent />
      </div>
    </SidebarCollapseProvider>
  )
}
